import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Clock } from 'lucide-react';
import { cn } from '../lib/cn';
import { controlClasses, useFieldContext } from './field-context';

/**
 * The time picker.
 *
 * The same argument as the date picker: `<input type="time">` is a different
 * control in every browser and cannot be styled. The value stays 24-hour
 * `HH:mm` because that is what the services store; the field displays 12-hour
 * with am/pm, matching `formatTime` in the web app and how a Philippine roster
 * is read aloud.
 */

const MINUTES_IN_DAY = 24 * 60;

/** `09:05` → 545. Returns `null` for anything that is not a 24-hour time. */
export function parseIsoTime(value: string | undefined | null): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function toIsoTime(totalMinutes: number): string {
  const wrapped = ((totalMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function formatDisplayTime(value: string | undefined): string {
  const total = parseIsoTime(value);
  if (total === null) return value ?? '';
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const meridiem = hours < 12 ? 'am' : 'pm';
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${String(minutes).padStart(2, '0')} ${meridiem}`;
}

/**
 * Reads a hand-typed time, or returns `null`.
 *
 * Rosters get typed fast and inconsistently — `9`, `930`, `9:05 am`, `0905`,
 * `21:30` are all the same intent. Anything that survives this function is
 * unambiguous; anything that does not is refused rather than rounded to
 * something plausible.
 */
export function parseTimeInput(text: string): string | null {
  const trimmed = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!trimmed) return null;

  const meridiemMatch = /(am|pm)\.?$/.exec(trimmed);
  const meridiem = meridiemMatch?.[1];
  const body = meridiem ? trimmed.slice(0, meridiemMatch.index) : trimmed;

  let hours: number;
  let minutes: number;

  const separated = /^(\d{1,2})[:.h](\d{2})$/.exec(body);
  const bare = /^(\d{1,4})$/.exec(body);
  if (separated) {
    hours = Number(separated[1]);
    minutes = Number(separated[2]);
  } else if (bare) {
    const digits = bare[1] as string;
    if (digits.length <= 2) {
      hours = Number(digits);
      minutes = 0;
    } else {
      // `930` is 9:30 and `0930` is 09:30 — the minutes are always the last two.
      hours = Number(digits.slice(0, digits.length - 2));
      minutes = Number(digits.slice(-2));
    }
  } else {
    return null;
  }

  if (minutes > 59) return null;
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'am') hours = hours === 12 ? 0 : hours;
    else hours = hours === 12 ? 12 : hours + 12;
  } else if (hours > 23) {
    return null;
  }

  return toIsoTime(hours * 60 + minutes);
}

export interface TimePickerProps {
  /** 24-hour `HH:mm`, or empty. */
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** 24-hour bounds. Times outside them are disabled in the lists. */
  min?: string;
  max?: string;
  disabled?: boolean;
  /** Granularity of the minute column. Typed entry is not restricted by it. */
  minuteStep?: number;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const columnItemClasses =
  'w-full rounded-md px-2 py-1 text-left text-base tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-40';

export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(function TimePicker(
  {
    value,
    onChange,
    onBlur,
    min,
    max,
    disabled,
    minuteStep = 15,
    placeholder = 'h:mm am',
    id,
    name,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  },
  ref,
) {
  const field = useFieldContext();
  const generatedId = useId();
  const inputId = field.id || id || generatedId;
  const errorId = `${generatedId}-parse-error`;

  const inputRef = useRef<HTMLInputElement | null>(null);
  /** True only for the render in which the panel is opening. */
  const scrollSelectionIntoView = useRef(false);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const total = parseIsoTime(value);
  const minTotal = parseIsoTime(min);
  const maxTotal = parseIsoTime(max);

  const step = minuteStep > 0 && minuteStep <= 60 ? minuteStep : 15;
  const minutes = useMemo(
    () => Array.from({ length: Math.ceil(60 / step) }, (_, index) => index * step),
    [step],
  );

  /**
   * Opening the panel on a scrolled-away hour is the classic way to lose the
   * current value, so the selection is centred on open — but *only* on open.
   * The flag is cleared after the first paint because by then the user's own
   * clicks are what change the selection, and a list that jumps under the
   * cursor is worse than one that stays put.
   */
  useEffect(() => {
    if (!open) {
      scrollSelectionIntoView.current = false;
      return;
    }
    scrollSelectionIntoView.current = true;
    const frame = requestAnimationFrame(() => {
      scrollSelectionIntoView.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  /**
   * A callback ref, not an effect: Radix mounts the portal on a render of its
   * own, so an effect here runs before there is anything to scroll.
   */
  const centreOnOpen = useCallback((node: HTMLButtonElement | null) => {
    if (!node || !scrollSelectionIntoView.current) return;
    node.scrollIntoView({ block: 'center' });
  }, []);

  const isOutOfRange = (candidate: number): boolean => {
    if (minTotal !== null && candidate < minTotal) return true;
    if (maxTotal !== null && candidate > maxTotal) return true;
    return false;
  };

  const commit = (candidate: number): void => {
    setDraft(null);
    setParseError(null);
    onChange(toIsoTime(candidate));
  };

  const commitDraft = (): void => {
    if (draft === null) return;
    const text = draft.trim();
    setDraft(null);
    if (!text) {
      setParseError(null);
      if (value) onChange('');
      return;
    }
    const iso = parseTimeInput(text);
    if (iso === null) {
      setParseError(`“${text}” is not a time. Try 9:05 am, 0905 or 09:05.`);
      return;
    }
    const candidate = parseIsoTime(iso) as number;
    if (isOutOfRange(candidate)) {
      const bound =
        minTotal !== null && candidate < minTotal
          ? `no earlier than ${formatDisplayTime(min)}`
          : `no later than ${formatDisplayTime(max)}`;
      setParseError(`The time must be ${bound}.`);
      return;
    }
    setParseError(null);
    onChange(iso);
  };

  /** Arrow keys nudge the value: minutes by the step, hours with Shift held. */
  const nudge = (direction: 1 | -1, byHour: boolean): void => {
    // An empty field starts at 9am rather than midnight, because a shift that
    // begins at 00:00 is not what anyone is reaching for by pressing an arrow.
    if (total === null) {
      commit(9 * 60);
      return;
    }
    const next = total + direction * (byHour ? 60 : step);
    if (isOutOfRange(((next % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY)) return;
    commit(next);
  };

  const onColumnKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const buttons = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
    );
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (index === -1) return;
    event.preventDefault();
    const next = buttons[index + (event.key === 'ArrowDown' ? 1 : -1)];
    next?.focus();
  };

  const displayValue = draft ?? formatDisplayTime(value);
  const describedBy =
    [field.describedBy, parseError ? errorId : null].filter(Boolean).join(' ') || undefined;
  const invalid = field.invalid || Boolean(parseError);
  const selectedHour = total === null ? null : Math.floor(total / 60);
  const selectedMinute = total === null ? null : total % 60;

  return (
    <div className={cn('min-w-0', className)}>
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div className="relative">
            <input
              ref={(node) => {
                inputRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
              }}
              id={inputId}
              type="text"
              autoComplete="off"
              disabled={disabled}
              placeholder={placeholder}
              value={displayValue}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={() => {
                commitDraft();
                onBlur?.();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitDraft();
                } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                  event.preventDefault();
                  // Half-typed text is committed first; nudging from a value
                  // the field does not hold yet would step from the old one.
                  if (draft !== null) commitDraft();
                  else nudge(event.key === 'ArrowUp' ? 1 : -1, event.shiftKey);
                } else if (event.key === 'Escape' && draft !== null) {
                  event.preventDefault();
                  setDraft(null);
                  setParseError(null);
                }
              }}
              className={cn(
                controlClasses,
                'h-9 pr-9',
                invalid
                  ? 'border-danger focus:ring-danger/25'
                  : 'border-line-strong focus:border-brand',
              )}
            />
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Choose time"
                className={cn(
                  'absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-r-md text-ink-muted transition-colors',
                  'hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
                  'disabled:cursor-not-allowed disabled:text-ink-subtle',
                )}
              >
                <Clock className="h-4 w-4" aria-hidden />
              </button>
            </PopoverPrimitive.Trigger>
          </div>
        </PopoverPrimitive.Anchor>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={4}
            aria-label="Time"
            className="z-50 w-[13rem] animate-fade-in rounded-lg border border-line bg-surface p-2 shadow-lg focus:outline-none motion-reduce:animate-none"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              inputRef.current?.focus();
            }}
          >
            <div className="grid grid-cols-2 gap-2">
              <div
                role="listbox"
                aria-label="Hour"
                onKeyDown={onColumnKeyDown}
                className="max-h-56 space-y-0.5 overflow-y-auto pr-1"
              >
                {Array.from({ length: 24 }, (_, hour) => hour).map((hour) => {
                  const candidate = hour * 60 + (selectedMinute ?? 0);
                  const isSelected = hour === selectedHour;
                  return (
                    <button
                      key={hour}
                      ref={isSelected ? centreOnOpen : undefined}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={isOutOfRange(candidate)}
                      onClick={() => commit(candidate)}
                      className={cn(
                        columnItemClasses,
                        isSelected
                          ? 'bg-brand font-medium text-white hover:bg-brand-hover'
                          : 'text-ink hover:bg-neutral-soft',
                      )}
                    >
                      {formatDisplayTime(toIsoTime(hour * 60)).replace(':00', '')}
                    </button>
                  );
                })}
              </div>
              <div
                role="listbox"
                aria-label="Minute"
                onKeyDown={onColumnKeyDown}
                className="max-h-56 space-y-0.5 overflow-y-auto pr-1"
              >
                {minutes.map((minute) => {
                  const candidate = (selectedHour ?? 9) * 60 + minute;
                  const isSelected = minute === selectedMinute;
                  return (
                    <button
                      key={minute}
                      ref={isSelected ? centreOnOpen : undefined}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={isOutOfRange(candidate)}
                      onClick={() => commit(candidate)}
                      className={cn(
                        columnItemClasses,
                        isSelected
                          ? 'bg-brand font-medium text-white hover:bg-brand-hover'
                          : 'text-ink hover:bg-neutral-soft',
                      )}
                    >
                      :{String(minute).padStart(2, '0')}
                    </button>
                  );
                })}
              </div>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {name ? <input type="hidden" name={name} value={value ?? ''} /> : null}

      {parseError ? (
        <p id={errorId} role="alert" className="mt-1.5 text-sm text-danger">
          {parseError}
        </p>
      ) : null}
    </div>
  );
});
